import type { AuthSession } from "@/types/auth";

export const AUTH_SESSION_COOKIE = "epitrack_auth_session";
export const AUTH_IDLE_TIMEOUT_MS = 15 * 60 * 1000;
export const AUTH_ACTIVITY_THROTTLE_MS = 30 * 1000;
export const AUTH_SESSION_EXPIRED_EVENT = "epitrack:auth:session-expired";
const AUTH_RECOVERY_MESSAGE_KEY = "epitrack_auth_recovery_message";
export const AUTH_SESSION_EXPIRED_MESSAGE =
  "Sua sessão expirou. Faça login novamente.";

function encodeSession(session: AuthSession) {
  return encodeURIComponent(JSON.stringify(session));
}

function decodeSession(value: string) {
  return JSON.parse(decodeURIComponent(value)) as AuthSession;
}

function readPasswordStateClaims(accessToken: string) {
  try {
    const encodedPayload = accessToken.split(".")[1];
    if (!encodedPayload) {
      return {};
    }

    const normalizedPayload = encodedPayload.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(
      atob(normalizedPayload.padEnd(Math.ceil(normalizedPayload.length / 4) * 4, "=")),
    ) as { password_expired?: unknown; must_change_password?: unknown };

    return {
      passwordExpired:
        typeof payload.password_expired === "boolean"
          ? payload.password_expired
          : undefined,
      mustChangePassword:
        typeof payload.must_change_password === "boolean"
          ? payload.must_change_password
          : undefined,
    };
  } catch {
    return {};
  }
}

export function serializeAuthSession(session: AuthSession) {
  return encodeSession(session);
}

export function parseAuthSession(value?: string | null) {
  if (!value) {
    return null;
  }

  try {
    const session = decodeSession(value);
    if (
      session.passwordExpired !== undefined &&
      session.mustChangePassword !== undefined
    ) {
      return session;
    }

    const claims = readPasswordStateClaims(session.accessToken);
    return {
      ...session,
      ...(claims.passwordExpired === undefined
        ? {}
        : { passwordExpired: claims.passwordExpired }),
      ...(claims.mustChangePassword === undefined
        ? {}
        : { mustChangePassword: claims.mustChangePassword }),
    };
  } catch {
    return null;
  }
}

export function readAuthSessionCookie() {
  if (typeof document === "undefined") {
    return null;
  }

  const cookie = document.cookie
    .split("; ")
    .find((entry) => entry.startsWith(`${AUTH_SESSION_COOKIE}=`));

  if (!cookie) {
    return null;
  }

  const session = parseAuthSession(cookie.split("=").slice(1).join("="));
  if (session && session.lastActivityAt === undefined) {
    const hydratedSession = { ...session, lastActivityAt: Date.now() };
    writeAuthSessionCookie(hydratedSession);
    return hydratedSession;
  }

  return session;
}

export function writeAuthSessionCookie(session: AuthSession) {
  document.cookie = [
    `${AUTH_SESSION_COOKIE}=${serializeAuthSession(session)}`,
    "Path=/",
    "SameSite=Lax",
  ].join("; ");
}

export function recordAuthActivity(now = Date.now()) {
  const session = readAuthSessionCookie();
  if (!session) {
    return null;
  }

  const lastActivityAt = session.lastActivityAt ?? now;
  if (now - lastActivityAt >= AUTH_IDLE_TIMEOUT_MS) {
    clearAuthSessionCookie();
    return null;
  }

  if (now - lastActivityAt < AUTH_ACTIVITY_THROTTLE_MS) {
    return session;
  }

  const nextSession = {
    ...session,
    lastActivityAt: now,
  };
  writeAuthSessionCookie(nextSession);
  return nextSession;
}

export function clearAuthSessionCookie() {
  document.cookie = [
    `${AUTH_SESSION_COOKIE}=`,
    "Path=/",
    "Max-Age=0",
    "SameSite=Lax",
  ].join("; ");
}

export function expireAuthSession(message = AUTH_SESSION_EXPIRED_MESSAGE) {
  clearAuthSessionCookie();
  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.setItem(AUTH_RECOVERY_MESSAGE_KEY, message);
  } catch {
    // A recuperação ainda funciona quando o armazenamento do navegador está bloqueado.
  }
  window.dispatchEvent(new CustomEvent<string>(AUTH_SESSION_EXPIRED_EVENT, {
    detail: message,
  }));
}

export function consumeAuthRecoveryMessage() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const message = window.sessionStorage.getItem(AUTH_RECOVERY_MESSAGE_KEY);
    window.sessionStorage.removeItem(AUTH_RECOVERY_MESSAGE_KEY);
    return message;
  } catch {
    return null;
  }
}

export function hasAuthSession(session: AuthSession | null | undefined) {
  return Boolean(session?.accessToken && session?.refreshToken);
}
